import React, { useState } from 'react';

import { TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import UserForm, { type DefaultValues } from './UserForm';
import Modal from '../../../components/Modal';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { type UsersRecordExtended } from '../../../types/apiTypes';
import AdminUsersContainer, { type UpdateUserInput } from '../AdminUsersContainer';
import { UserRole } from '../constants/user-roles';
import { Footer, ModalButton, ModalContent } from '../styles';
import { ActionsTableCell, StyledActionButton, StyledTableCell } from '../styles/table-styles';

const StyledTableRow = styled(TableRow)<{ $pendingChanges: boolean }>`
    background-color: ${(props) => (props.$pendingChanges ? props.theme.colors.loadingBackground : 'transparent')};
`;

interface UserControlProps {
    user: UsersRecordExtended;
}

const StyledTypography = styled(Typography)`
    margin-bottom: 2rem;
`;

export const systemRoles: UserRole[] = [UserRole.Admin, UserRole.Teacher, UserRole.InstructionalDesigner];

const UserControl: React.FC<UserControlProps> = ({ user }) => {
    const { updateUser, deleteUser } = useContainer(AdminUsersContainer);

    const { t } = useTranslation('admin_settings');

    // Edit Modal Logic
    const [editModalOpen, setEditModalOpen] = useState(false);
    const handleEditModalOpen = () => setEditModalOpen(true);
    const handleEditModalClose = () => setEditModalOpen(false);

    // Delete Modal Logic
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const handleDeleteModalOpen = () => setDeleteModalOpen(true);
    const handleDeleteModalClose = () => setDeleteModalOpen(false);

    const { data: orgSettings } = useOrgSettings();

    const onEditUserSubmit = (values: DefaultValues) => {
        const updates: UpdateUserInput = {
            user_id: user.user_id,
            email: values?.email ?? '',
            ext_user_id: values?.ext_user_id ?? '',
            user_name: values?.user_name ?? '',
            roles: values.roles ?? []
        };

        if (values.password) {
            updates.password = values.password;
        }

        updateUser(updates);

        handleEditModalClose();
    };

    const handleDeleteUser = () => {
        deleteUser(user);

        handleDeleteModalClose();
    };

    const renderStringOfRoles = (roles: (string | UserRole)[]) => {
        const localizedRoles = roles.map((role) => {
            if (systemRoles.includes(role as UserRole)) {
                return t(`users.userRoles.${role}`);
            }
            return role;
        });

        return localizedRoles.sort().map((role, index) => (
            <React.Fragment key={index}>
            {role}
            <br />
            </React.Fragment>
        ));
    };

    return (
        <StyledTableRow $pendingChanges={Boolean(user.pendingChanges)}>
            <StyledTableCell>{user.user_name}</StyledTableCell>
            <StyledTableCell>{user.email}</StyledTableCell>
            {orgSettings?.featureSettings.options.useIdentifierInRoster && <StyledTableCell>{user.ext_user_id}</StyledTableCell>}
            <StyledTableCell>
                <Typography>{renderStringOfRoles(user?.roles ?? [])}</Typography>
            </StyledTableCell>
            <ActionsTableCell>
                <StyledActionButton variant="outlined" onClick={handleEditModalOpen}>
                    {t('users.actions.edit-user')}
                </StyledActionButton>
                <StyledActionButton variant="outlined" onClick={handleDeleteModalOpen}>
                    {t('users.actions.delete-user')}
                </StyledActionButton>
            </ActionsTableCell>

            <Modal isOpen={editModalOpen} onClose={handleEditModalClose}>
                <StyledTypography variant="h5">{t('users.dialogs.edit-user-title')}</StyledTypography>
                <UserForm
                    onSubmit={onEditUserSubmit}
                    initialValues={{
                        email: user.email,
                        user_name: user.user_name,
                        ext_user_id: user.ext_user_id,
                        password: '',
                        roles: user.roles
                    }}
                    handleModalClose={handleEditModalClose}
                />
            </Modal>

            <Modal isOpen={deleteModalOpen} onClose={handleDeleteModalClose}>
                <Typography variant="h5">{t('users.dialogs.delete-user-title')}</Typography>
                <ModalContent>
                    <Typography>
                        {t('users.dialogs.delete-user-warning-message', { userName: user.user_name })}
                    </Typography>
                </ModalContent>

                <Footer>
                    <ModalButton onClick={handleDeleteModalClose} color="secondary" variant="outlined">
                        {t('users.actions.cancel')}
                    </ModalButton>
                    <ModalButton onClick={handleDeleteUser} disableElevation variant="contained" color="error">
                        {t('users.actions.delete')}
                    </ModalButton>
                </Footer>
            </Modal>
        </StyledTableRow>
    );
};

export default UserControl;
